import React from 'react';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import type { ProposalDetail } from '@/lib/proposals/core/types';

import { useProposalSelection } from '../../lib/proposals/hooks/ProposalSelectionContext';
import { useExecutionStatus } from './shared/useExecutionStatus';

/**
 * Already-sent + failed-execution banners for a proposal's primary action.
 * Render in the CONTENT region's banner stack (ProposalDetailPanel, beside
 * PartialExecutionBanner et al.) — moved out of StickyFooter so the footer
 * holds only the chat input and the approve/reject buttons (task 4 part 2a,
 * 2026-09-01). Derivation is the shared useExecutionStatus hook, the same
 * one gating the approve button, so banner and button can never disagree.
 * Raw per-event failure detail is logged (StickyFooter's effect), never
 * rendered here.
 */
export function ExecutionStatusBanners({
  action,
  selectedActionIds,
}: {
  action: ProposalDetail;
  /** Live checkbox state — deselecting a failed action lifts the verdict. */
  selectedActionIds?: number[];
}) {
  const {
    isEmailAction,
    actionAlreadyExecuted,
    actionExecutedAt,
    actionExecutionFailed,
    actionPermanentlyFailed,
    failedExecutionGuidance,
    primaryAction,
  } = useExecutionStatus(action, selectedActionIds);
  const displayTimezone = useDisplayTimezone();

  if (!(actionAlreadyExecuted && isEmailAction) && !actionExecutionFailed) {
    return null;
  }

  return (
    <div className='mx-3 mt-3 flex flex-col gap-3 sm:mx-4 lg:mx-6'>
      {actionAlreadyExecuted && isEmailAction && (
        <div
          className='flex items-start gap-3 p-3 rounded-lg bg-utility-warning-50 border border-utility-warning-200 text-utility-warning-800'
          role='alert'
        >
          <svg
            className='w-6 h-6 text-utility-warning-600 flex-shrink-0'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <div className='flex-1'>
            <div className='font-semibold'>
              This email has already been sent
            </div>
            {actionExecutedAt && (
              <div className='text-sm mt-1'>
                Sent on{' '}
                {new Date(actionExecutedAt).toLocaleString(undefined, {
                  timeZone: displayTimezone,
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short',
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {actionExecutionFailed && (
        <div
          className='flex items-start gap-3 p-3 rounded-lg bg-utility-error-50 border border-utility-error-200 text-utility-error-800'
          role='alert'
        >
          <svg
            className='w-6 h-6 text-utility-error-600 flex-shrink-0'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <div className='flex-1'>
            <div className='font-semibold'>
              {actionPermanentlyFailed
                ? 'Action failed'
                : 'Previous attempt failed'}
            </div>
            <div className='text-sm mt-1'>
              We couldn't complete this action.
              {failedExecutionGuidance}
            </div>
            {primaryAction?.execution_result?.validation_errors && (
              <div className='text-sm mt-2'>
                {primaryAction.execution_result.validation_errors.length === 1
                  ? "1 event couldn't be applied."
                  : `${primaryAction.execution_result.validation_errors.length} events couldn't be applied.`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Selection-aware wrapper — reads the live checkbox state from
 * ProposalSelectionContext so deselecting a failed action updates the
 * banner immediately, without re-rendering the caller. Mirrors
 * SelectionAwareStickyFooter.
 */
export const SelectionAwareExecutionStatusBanners = React.memo(
  function SelectionAwareExecutionStatusBanners({
    action,
  }: {
    action: ProposalDetail;
  }) {
    const { selectedActionIds } = useProposalSelection();
    return (
      <ExecutionStatusBanners
        action={action}
        selectedActionIds={selectedActionIds}
      />
    );
  }
);
